import { randomID } from './S';
import axios, { isAxiosError } from 'axios';
import { globalStore } from '../GlobalStore';
import { Storage } from './Storage';

export interface Request<T> {
  requestId: string;
  traceId: string;
  token: string;
  /** 与 tenantId 同值，兼容旧字段名 */
  teamId: string;
  /** seller Request 模型实际字段名 */
  tenantId: string;
  parameter: T;
}

export interface Response<T> {
  success: boolean;
  result: T;
  costTime: number;
  errorCode: string;
  errorMessages: string | string[];
}

const isDateString = (value: unknown): boolean =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value);

const convertDates = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(convertDates);
  }
  if (obj && typeof obj === 'object') {
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = (obj as Record<string, unknown>)[key];
        if (isDateString(val)) {
          (obj as Record<string, unknown>)[key] = new Date(val as string);
        } else if (val && typeof val === 'object') {
          (obj as Record<string, unknown>)[key] = convertDates(val);
        }
      }
    }
  }
  return obj;
};

const formatDateToString = (date: Date): string => {
  if (!date) return '未知';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const convertDatesToString = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(convertDatesToString);
  }
  if (obj && typeof obj === 'object') {
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const val = (obj as Record<string, unknown>)[key];
        if (val instanceof Date) {
          (obj as Record<string, unknown>)[key] = formatDateToString(val);
        } else if (val && typeof val === 'object') {
          (obj as Record<string, unknown>)[key] = convertDatesToString(val);
        }
      }
    }
  }
  return obj;
};

export const API_DOMAIN = process.env.REACT_APP_API_DOMAIN;

/** post 失败时返回的 `errorCode|errorMessage` 解析为展示文案 */
export const parsePostError = (errorInfo: string): string => {
  const [code, ...rest] = errorInfo.split('|');
  return rest.join('|') || code;
};

/** post 失败时返回 errorInfo 字符串；成功时 result 也可能是 string（如新建 ID） */
export const isPostErrorResult = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  if (value.includes('|')) {
    return true;
  }
  return value === 'NETWORK';
};

const joinApiUrl = (domain: string | undefined, path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  if (!domain) {
    return path.startsWith('/') ? path : `/${path}`;
  }
  const normalizedDomain = domain.replace(/\/+$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedDomain}${normalizedPath}`;
};

type PostOptions = {
  quiet?: boolean;
  logTag?: string;
};

export const post = async <T, R>(
  url: string,
  inputParameter: T,
  options?: PostOptions,
): Promise<R | string> => {
  let status = false;
  let result: R | null = null;
  let error: unknown = null;
  let errorCode = '';

  try {
    const traceId = randomID();
    const token = (await Storage.getItem('token')) || localStorage.getItem('token');
    const teamId =
      (await Storage.getItem('teamId')) || localStorage.getItem('teamId') || '';
    const parameter = convertDatesToString(inputParameter) as T;
    const requestData: Request<T> = {
      requestId: traceId,
      traceId,
      token: (token as string) || '',
      teamId,
      tenantId: teamId,
      parameter,
    };

    const response = await axios.post<Response<R>>(joinApiUrl(API_DOMAIN, url), requestData);
    status = response.data.success;

    if (status) {
      result = convertDates(response.data.result) as R;
    } else {
      errorCode = response.data.errorCode;
      if (errorCode === '0000111') {
        if (globalStore.isLoggedIn) {
          await globalStore.clearSession();
          if (!window.location.hash.includes('login')) {
            window.location.hash = '#/login';
          }
        }
      }
      const errorMsgs = response.data.errorMessages;
      if (Array.isArray(errorMsgs) && errorMsgs.length > 0) {
        error = errorMsgs[0];
      } else if (typeof errorMsgs === 'string') {
        error = errorMsgs;
      } else {
        error = `错误码：${errorCode}`;
      }
    }
  } catch (e) {
    if (isAxiosError(e)) {
      const data = e.response?.data as Partial<Response<R>> | undefined;
      if (data && typeof data === 'object' && 'success' in data) {
        errorCode = data.errorCode || String(e.response?.status ?? '');
        const errorMsgs = data.errorMessages;
        if (Array.isArray(errorMsgs) && errorMsgs.length > 0) {
          error = errorMsgs[0];
        } else if (typeof errorMsgs === 'string') {
          error = errorMsgs;
        } else {
          error = e.message;
        }
      } else {
        errorCode = String(e.response?.status ?? 'NETWORK');
        error = e.message;
      }
      const errorInfo = error ? `${errorCode}|${error}` : errorCode;
      return errorInfo as string;
    }
    throw new Error(errorCode || String(e));
  }

  if (status) {
    return result as R;
  }
  const errorInfo = error ? `${errorCode}|${error}` : errorCode;
  return errorInfo as string;
};

export const postOrThrow = async <T, R>(url: string, inputParameter: T, options?: PostOptions): Promise<R> => {
  const result = await post<T, R>(url, inputParameter, options);
  if (isPostErrorResult(result)) {
    throw new Error(result);
  }
  return result as R;
};
